import { normalizeAdTokenText, type AdToken } from "@/shared/adTokens/adTokens";

/*
    What a touch gets per recipient, filled from the audience row rather than the registry.

    Mirrors `PERSON_TOKENS` in `sequence/services/render.py`. Deliberately not registered:
    `/ad-tokens/` serves every definition to every picker on the platform, and a token that
    nothing can fill in an ad has no business being offered to a copywriter. They are merge
    fields on a sequence send and nowhere else, so they live with the screens that send.

    Shaped as `AdToken` so they can join the registry's list wherever a vocabulary is needed
    -- the picker, the highlighter and the check all take the same array.
*/
export const PERSON_TOKENS: AdToken[] = [
    {
        id: -1, token: "[FIRST NAME]", scope: "recipient", source: "recipient_field",
        help_text: "The student's first name, from the audience. Falls back to \"there\".",
        record_field: "first_name", is_automatable: true, aliases: ["[first_name]"],
    },
    {
        id: -2, token: "[LAST NAME]", scope: "recipient", source: "recipient_field",
        help_text: "The student's last name, from the audience.",
        record_field: "last_name", is_automatable: true, aliases: ["[last_name]"],
    },
];

/*
    One spelling for a token however it is written -- the registry's rule, not a second one.

    This used to fold `_` to a space as `render.py::_key` does, which left two different
    rules running on the same screen: the shared check classified `[APPLY_LINK]` unknown
    while the sample list and preview beside it resolved it. One of the two had to go, and
    the shared one stays, so a bracket is classified identically here and in ad copy.

    Underscore spellings still resolve, through the registry's ALIASES rather than through
    folding -- `[school_short]` is a registered alias of `[SCHOOL SHORT NAME]`, and
    `[first_name]` an alias on PERSON_TOKENS above. A spelling nobody registered, like
    `[APPLY_LINK]`, now reads as unknown everywhere on the screen rather than in one half
    of it.
*/
export function tokenKey(token: string): string {
  return normalizeAdTokenText(token.trim()).replace(/^\[|\]$/g, "").trim();
}

/*
    The merge fields the templating engine knows, as a lookup.

    Built from `GET /api/v1/content/ad-tokens/` rather than held here: the registry is the
    database model that owns this vocabulary, it carries 93 tokens against the dozen this file
    used to list, and a token added in admin has to be known to the editor without a release.
    Aliases count -- a writer who types the registry's `[school_short]` has written the same
    token as `[SCHOOL SHORT NAME]`, and the server resolves both.
*/
export type TokenVocabulary = {
  known: (key: string) => boolean;
  /** The registry's own spelling for a key, for inserting. Falls back to the key as written. */
  canonical: (key: string) => string;
};

/** A vocabulary that knows nothing, for a registry read that has not landed or has failed. */
export const NO_VOCABULARY: TokenVocabulary = { known: () => false, canonical: tokenBracketed };

export function vocabularyOf(tokens: AdToken[]): TokenVocabulary {
  const canonicals = new Map<string, string>();
  for (const token of PERSON_TOKENS) {
    canonicals.set(tokenKey(token.token), token.token);
    for (const alias of token.aliases) canonicals.set(tokenKey(alias), token.token);
  }
  for (const token of tokens) {
    canonicals.set(tokenKey(token.token), token.token);
    for (const alias of token.aliases) {
      if (!canonicals.has(tokenKey(alias))) canonicals.set(tokenKey(alias), token.token);
    }
  }
  return {
    known: (key) => canonicals.has(tokenKey(key)),
    canonical: (key) => canonicals.get(tokenKey(key)) ?? tokenBracketed(key),
  };
}

export const STOP_LINE = "Reply STOP to opt out";

/*
    Segments decide the bill, and the encoding decides the segment size: one character
    outside GSM-7 — a curly quote pasted from Word is the usual culprit — drops the limit
    from 160 to 70 and turns a one-segment text into three.
*/
export type SmsCount = { n: number; seg: number; enc: "GSM-7" | "UCS-2" };

/*
    Tokens replaced by what they will actually resolve to. Segment counting has to run on
    this, never on the source: `[first_name]` is 12 characters of brackets that never send,
    and the brackets themselves fall outside GSM-7, so counting the source misclassifies
    every tokenised message as UCS-2 and halves its limit from 160 to 70.
*/
export function resolveCopy(
  text: string,
  values: Record<string, string>,
  vocabulary: TokenVocabulary = NO_VOCABULARY,
): string {
  return (text || "").replace(ANY_TOKEN, (all, key: string) => {
    const value = lookupToken(key, values, vocabulary);
    return value === undefined ? all : value;
  });
}

/*
    Two spellings of the same vocabulary reach this function, and both are real.

    The registry's canonical is `[BRAND NAME]` — upper case, spaces — and that is what the
    loaded playbook copy contains. Its aliases carry the snake_case spelling the sequence
    screens were drawn against, `[school_short]`, which Ops may also type.

    `values` arrives keyed either way, so both sides go through `tokenKey` and meet. A token
    the vocabulary does not know is left standing rather than blanked: a bracket the reader
    can see is a better failure than a silent gap in a subject line.
*/
/*
    The same bracket string the rest of the platform finds.

    Character class and bounds copied from `src/shared/adTokens/adTokenScan.ts`, which the
    server's `content/services/ad_token_scan.py` mirrors deliberately ("kept equal so both
    find the same bracket strings"). Without the newline exclusion and the 128 bound a stray
    `[` swallows everything up to the next `]` further down the copy — so these screens would
    have resolved a token the editor and the server agree is not one. The capture group is
    the only addition; the scanner needs the brackets, a lookup needs what is inside them.
*/
const ANY_TOKEN = /\[([^[\]\n]{1,128})\]/g;

export function lookupToken(
  key: string,
  values: Record<string, string>,
  vocabulary: TokenVocabulary = NO_VOCABULARY,
): string | undefined {
  /*
      An alias is looked up under its CANONICAL too, which is the half that was missing.

      `CATALOG_PREVIEW_VALUES` is keyed by canonical only, so copy containing `[link]` found
      nothing and previewed amber as "no sample value" -- while `[APPLY LINK]`, the same
      token, showed its value two inches below, and the editor's own note said "[link] will
      be read as [APPLY LINK]". Three parts of one screen disagreeing about one token.
  */
  const direct = _read(key, values);
  if (direct !== undefined) return direct;

  const canonical = vocabulary.canonical(key);
  if (tokenKey(canonical) !== tokenKey(key)) {
    const viaCanonical = _read(canonical, values);
    if (viaCanonical !== undefined) return viaCanonical;
  }

  return vocabulary.known(key) ? "" : undefined;
}

/** One `values` entry whose key is the same token as `key`, or undefined for no entry. */
function _read(key: string, values: Record<string, string>): string | undefined {
  const wanted = tokenKey(key);
  for (const [name, value] of Object.entries(values)) {
    if (tokenKey(name) === wanted) return value ?? "";
  }
  return undefined;
}

/** What the recipient's handset counts: the resolved message plus the opt-out line. */
export function smsCountFor(
  text: string,
  values: Record<string, string>,
  vocabulary: TokenVocabulary = NO_VOCABULARY,
): SmsCount {
  return smsSegments(`${resolveCopy(text, values, vocabulary)}\n${STOP_LINE}`);
}

export function smsSegments(text: string): SmsCount {
  const gsm = /^[A-Za-z0-9 @$_!"#%&'()*+,\-./:;<=>?\n\r]*$/.test(text);
  const n = text.length;
  if (gsm) return { n, seg: n <= 160 ? 1 : Math.ceil(n / 153), enc: "GSM-7" };
  return { n, seg: n <= 70 ? 1 : Math.ceil(n / 67), enc: "UCS-2" };
}

export type Segment = { t: string; token?: string; empty?: boolean; unknown?: boolean };

/*
    Splits copy into plain runs and token runs so the preview can show each token filled in
    with sample data, and flag the two failure cases a reader must see: a token with no value
    for this campaign, and a token the engine does not know.
*/
export function segmentCopy(
  text: string,
  values: Record<string, string>,
  vocabulary: TokenVocabulary = NO_VOCABULARY,
): Segment[] {
  const out: Segment[] = [];
  const pattern = new RegExp(ANY_TOKEN.source, "g");
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push({ t: text.slice(last, match.index) });
    const key = match[1];
    // Through the same lookup as resolveCopy, so what the preview highlights and what the
    // message actually sends cannot disagree about which tokens resolve.
    const value = lookupToken(key, values, vocabulary);
    const known = value !== undefined;
    out.push({
      t: value ? value : match[0],
      token: key,
      empty: known && !value,
      unknown: !known,
    });
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push({ t: text.slice(last) });
  return out;
}

export function tokensIn(text: string): string[] {
  return Array.from(text.matchAll(ANY_TOKEN)).map((m) => m[1]);
}

/** A token as it is written, whichever vocabulary it came from. */
export function tokenBracketed(key: string): string {
  return key.startsWith("[") ? key : `[${key}]`;
}

/** The body HTML our templating engine produces, split back into previewable blocks. */
export type BodyBlock =
  | { kind: "heading"; text: string }
  | { kind: "para"; text: string }
  | { kind: "button"; text: string };

export function parseBody(html: string): BodyBlock[] {
  const blocks: BodyBlock[] = [];
  const pattern = /<(h[12])\b[^>]*>([\s\S]*?)<\/\1>|<p\b[^>]*>([\s\S]*?)<\/p>|<a\b[^>]*class="button"[^>]*>([\s\S]*?)<\/a>/gi;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(html)) !== null) {
    const strip = (value: string) => value.replace(/<[^>]+>/g, "").trim();
    if (match[2] != null) blocks.push({ kind: "heading", text: strip(match[2]) });
    else if (match[3] != null) blocks.push({ kind: "para", text: strip(match[3]) });
    else if (match[4] != null) blocks.push({ kind: "button", text: strip(match[4]) });
  }
  return blocks;
}

/** Keeps the structured Button label in step with a raw-HTML edit. */
export function ctaFromBody(html: string): { cta?: string; href?: string } {
  const anchor = /<a\b[^>]*class="button"[^>]*>([\s\S]*?)<\/a>/i.exec(html);
  if (!anchor) return {};
  const href = /href="([^"]*)"/i.exec(anchor[0]);
  return {
    cta: anchor[1].replace(/<[^>]+>/g, "").trim(),
    href: href ? href[1] : undefined,
  };
}

export function bodyWithCta(html: string, cta: string): string {
  return html.replace(
    /(<a\b[^>]*class="button"[^>]*>)([\s\S]*?)(<\/a>)/i,
    (_all, open: string, _text: string, close: string) => `${open}${cta}${close}`,
  );
}

/*
    Plain paragraphs out of stored HTML, and back again (MPN-2201).

    The ticket asks for structured fields with "raw HTML only behind an Advanced toggle", but
    the Body copy box has been showing the stored HTML all along -- tags included -- while its
    own hint said "Plain paragraphs". An Ops author editing a subject line was being handed
    markup to not break.

    Round-tripping is deliberately lossy in one direction only: the button anchor is dropped
    from the plain view, because it has its own Button label field and two places to edit one
    string is how they come to disagree. `htmlFromParagraphs` puts it back from that field.
*/
export function paragraphsFromHtml(html: string): string {
  return parseBody(html)
    .filter((block) => block.kind !== "button")
    .map((block) => block.text)
    .join("\n\n");
}

export function htmlFromParagraphs(text: string, html: string): string {
  const paragraphs = text
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => `<p>${part}</p>`)
    .join("\n");

  /*
      The button is carried over from the HTML being replaced rather than rebuilt, so its
      href survives. That href is the campaign's tracked [APPLY LINK] token, and a rebuild
      from the label alone would quietly drop it -- which is a send that goes nowhere.
  */
  const anchor = /<a\b[^>]*class="button"[^>]*>[\s\S]*?<\/a>/i.exec(html);
  return anchor ? `${paragraphs}\n${anchor[0]}` : paragraphs;
}
